<script lang="ts">
    import { onMount, getContext } from 'svelte';
    import { getCurrentFitnessSignature, getCurrentTrainingLoads, projectUserFitness, type XSSDetails } from '@/Util/utils';
    import { 
        type ProgramFocusTypes,
        defaultContinuousPlan,
        type ContinuousPlan,
        type XataFormProps,
        ProgramType,
        specificityValues,
        SpecificityTypes,
    } from "@/Common/constants";
    import type { MyFitnessStores } from '@/MyFitness/stores';
    import { createUserFocusPowers } from './modules/UserFocusPowers';
    import { writable } from 'svelte/store';
    import { FocusSlider } from './ProgramElements/SetupFormInputs';
    import XataGeneralSettings from './ProgramElements/XataGeneralSettings.svelte';
    import { FocusPowerIncrease, ProgramAchievabilityMessage, ProgramFitnessSignature, ProgramTrainingLoad } from './ProgramElements';
    import { type AchievabilityStatus, createProgramAchievability } from './modules/ProgramAchievability';
    import { addDays, differenceInDays } from 'date-fns';
    import TrainPrefDayPicker from './ProgramElements/TrainPrefDayPicker.svelte';

    const { userParams, trainingAdvice } = getContext<MyFitnessStores>('myFitness')
    let currentSignature: XSSDetails
    let currentTL: XSSDetails
    
    function reactiveUpdate() {
        currentSignature = getCurrentFitnessSignature($trainingAdvice)
        currentTL = getCurrentTrainingLoads($trainingAdvice)
        focusPower.updateFocusPower($plan.targetAthlete)
        if(projectedFS) setProjectedFitness() // Ensures it doesn't run on first load
    }
    // $: if($trainingAdvice) reactiveUpdate()
    $: if($trainingAdvice && !disabled) reactiveUpdate()

    let athleteTypes = Object.keys($userParams.athletes) as ProgramFocusTypes[]

    type $$Props = XataFormProps<ContinuousPlan>
    export let plan = writable({...defaultContinuousPlan})
    let className = ""
    export {className as class}
    export let disabled = false
    export let achievabilityId = "challenge-achievability"
    export let achievability: AchievabilityStatus

    $: achievability = $overallAchievability

    /* Current Focus Power and Duration */
    let focusPower = createUserFocusPowers(trainingAdvice)
    const { focusPowerDuration, currentFocusPower } = focusPower

    /* Achievability */
    const programAchievability = createProgramAchievability(ProgramType.Continuous)
    const {targetAchievability, overallAchievability, statusMessage} = programAchievability

    /* Projected FS, TL and Focus */
    let projectedFS: XSSDetails
    let projectedTL: XSSDetails
    let projectedFocusPower: number

    /* Date for projection */
    const projectedDate = addDays(new Date(), 30)

    async function setProjectedFitness() {
        const projectedUserFitness = await projectUserFitness(
            currentSignature, currentTL, $userParams.ir_params, $plan.irValue, 
            specificityValues[SpecificityTypes.Mixed], $userParams.athletes[$plan.targetAthlete].duration, 
            0, differenceInDays(projectedDate, new Date()), 
            ProgramType.Continuous, $plan.settings.difficulty
        )
        projectedFS = projectedUserFitness.sig
        projectedTL = projectedUserFitness.tl
        projectedFocusPower = projectedUserFitness.fp
        programAchievability.validateRampRates($plan.irValue, currentTL)
    }

    onMount(() => {
        focusPower.updateFocusPower($plan.targetAthlete)
        setProjectedFitness()
    })


</script>

<div class={className}>
    <div class="flex flex-col gap-5 flex-1 md:flex-row">
        <div class="basis-2/5 grid grid-cols-[2fr,5fr] auto-rows-max items-start gap-y-8 gap-x-3 md:gap-x-6 md:text-right text-left text-base md:grid">

            <span class="text-slate-600">Focus</span>
            <FocusSlider {athleteTypes} {disabled}
                bind:targetAthlete={$plan.targetAthlete}
                onInput={() => {
                    focusPower.updateFocusPower($plan.targetAthlete)
                    setProjectedFitness()
                }}
                focusPowerDuration={$focusPowerDuration}
            />

            <span class="text-slate-600">Improvement Rate</span>
            <select class="input-xfai" {disabled}
                bind:value={$plan.irValue}
                on:change={() => {
                    setProjectedFitness()
                }}
            >
                {#each $userParams.training_gradients as ir}
                        <option value="{ir['value']}">{ir['text']}</option>
                {/each}
            </select>

            <TrainPrefDayPicker class='col-span-2' bind:cal_settings={$plan.cal_settings} />

            <XataGeneralSettings class="col-span-2" {disabled} {plan} />
            
        </div>

        <div class="h-px md:w-px md:h-auto bg-black/10"/>

        <div class="basis-3/5 flex flex-col gap-10 text-base">
            <FocusPowerIncrease 
                currentFocusPower={$currentFocusPower} {projectedFocusPower}
                date={projectedDate} focusPowerDuration={$focusPowerDuration}
            />

            <div id={achievabilityId} class="flex flex-col gap-3 text-left">
                <span class="font-bold">
                    Achievability
                </span>
                <ProgramAchievabilityMessage statusMessage={$statusMessage} status={$overallAchievability}/>
            </div>

            <ProgramFitnessSignature 
                {currentSignature} targetAchievability={$targetAchievability} targetSignature={projectedFS}
                description = "Projected after 30 days" estimatedRowName="Projected"
            />

            <ProgramTrainingLoad 
                {currentTL} targetAchievability={$targetAchievability} targetTL={projectedTL}
                description = "Projected after 30 days" estimatedRowName="Projected"
            />
        </div>
    </div>
</div>